"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
// Tree-shaken icon imports for optimal bundle size
import Share2 from "lucide-react/dist/esm/icons/share-2";
import Copy from "lucide-react/dist/esm/icons/copy";
import Check from "lucide-react/dist/esm/icons/check";
import MessageCircle from "lucide-react/dist/esm/icons/message-circle";
import FacebookIcon from "lucide-react/dist/esm/icons/facebook";
import { cn } from "@/lib/utils";

// Twitter/X Icon component (lucide-react doesn't have official X icon yet)
const XIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 4l11.733 16h4.267l-11.733 -16z" />
    <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" />
  </svg>
);

interface ShareButtonProps {
  url: string;
  title: string;
  description?: string;
  variant?: 'default' | 'outline';
  size?: 'sm' | 'default' | 'lg';
  showDropdown?: boolean; // Show WhatsApp option in dropdown
  className?: string;
  onBeforeOpen?: () => Promise<void>; // Async callback before opening dropdown
}

export function ShareButton({
  url,
  title,
  description,
  variant = 'outline',
  size = 'sm',
  showDropdown = false,
  className,
  onBeforeOpen
}: ShareButtonProps) {
  const { toast } = useToast();
  const [canShare, setCanShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Check if native share is available
  useEffect(() => {
    setCanShare(!!navigator.share);
  }, []);

  // Handle native share API
  const handleNativeShare = async () => {
    const shareData: ShareData = {
      title,
      text: description || title,
      url,
    };

    try {
      await navigator.share(shareData);
      setIsDropdownOpen(false);
    } catch (error: any) {
      // User cancelled or share failed
      if (error.name !== 'AbortError') {
        console.warn('Share API failed:', error);
        // Fall through to clipboard
        handleCopyToClipboard();
      }
    }
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast({
        title: "הקישור הועתק!",
        description: "הקישור לשיתוף הועתק ללוח",
      });
      setTimeout(() => setCopied(false), 2000);
      setIsDropdownOpen(false);
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      setCopied(true);
      toast({
        title: "הקישור הועתק!",
        description: "הקישור לשיתוף הועתק ללוח",
      });
      setTimeout(() => setCopied(false), 2000);
      setIsDropdownOpen(false);
    }
  };

  // Handle WhatsApp share - properly encode Hebrew URLs
  const handleWhatsAppShare = () => {
    // WhatsApp accepts the raw URL with Hebrew characters
    // Don't use encodeURIComponent on the entire URL, just the text
    const text = `${title}\n${url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
    setIsDropdownOpen(false);
  };

  // Handle Facebook share
  const handleFacebookShare = () => {
    // Facebook sharer accepts the raw URL and will decode it properly
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
    setIsDropdownOpen(false);
  };

  // Handle X (Twitter) share
  const handleXShare = () => {
    // Twitter accepts both text and URL parameters
    const text = title;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
    setIsDropdownOpen(false);
  };

  // Simple button (no dropdown) - for mobile or when dropdown is disabled
  if (!showDropdown) {
    return (
      <Button
        variant={variant}
        size={size}
        className={cn(
          "gap-2",
          variant === 'outline' && "border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-800",
          className
        )}
        onClick={async () => {
          // Try native share first (mobile/modern browsers)
          if (canShare) {
            try {
              await navigator.share({ title, text: description || title, url });
              return;
            } catch (error: any) {
              if (error.name !== 'AbortError') {
                console.warn('Share API failed, falling back to clipboard');
              } else {
                return; // User cancelled
              }
            }
          }

          // Fallback to clipboard
          handleCopyToClipboard();
        }}
      >
        {copied ? (
          <>
            <Check className="h-4 w-4" />
            הועתק!
          </>
        ) : (
          <>
            <Share2 className="h-4 w-4" />
            שתף
          </>
        )}
      </Button>
    );
  }

  // Dropdown version (for desktop with WhatsApp option)
  return (
    <div className="relative" dir="rtl">
      <Button
        variant={variant}
        size={size}
        className={cn(
          "gap-2",
          variant === 'outline' && "border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-800",
          className
        )}
        onClick={async () => {
          // Call onBeforeOpen if provided (e.g., to generate share token)
          if (onBeforeOpen && !isDropdownOpen) {
            await onBeforeOpen();
          }
          setIsDropdownOpen(!isDropdownOpen);
        }}
      >
        {copied ? (
          <>
            <Check className="h-4 w-4" />
            הועתק!
          </>
        ) : (
          <>
            <Share2 className="h-4 w-4" />
            שתף
          </>
        )}
      </Button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsDropdownOpen(false)}
          />

          {/* Dropdown content - positioned right-0 for RTL, with max width to fit screen */}
          <div className="absolute right-0 mt-2 bg-white shadow-lg rounded-lg border border-orange-200 z-40 overflow-hidden w-56 max-w-[calc(100vw-2rem)]">
            {/* Native share (mobile only) */}
            {canShare && (
              <button
                onClick={handleNativeShare}
                className="flex items-center gap-3 w-full px-4 py-3 hover:bg-orange-50 transition-colors text-right"
              >
                <Share2 className="h-5 w-5 text-orange-600" />
                <span className="font-medium text-gray-700">שתף באפליקציה...</span>
              </button>
            )}

            {/* WhatsApp share */}
            <button
              onClick={handleWhatsAppShare}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-orange-50 transition-colors text-right"
            >
              <MessageCircle className="h-5 w-5 text-green-600" />
              <span className="font-medium text-gray-700">שתף בוואטסאפ</span>
            </button>

            {/* Facebook share */}
            <button
              onClick={handleFacebookShare}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-orange-50 transition-colors text-right"
            >
              <FacebookIcon className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-gray-700">שתף בפייסבוק</span>
            </button>

            {/* X (Twitter) share */}
            <button
              onClick={handleXShare}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-orange-50 transition-colors text-right"
            >
              <XIcon className="h-5 w-5 text-gray-900" />
              <span className="font-medium text-gray-700">שתף ב-X</span>
            </button>

            {/* Copy to clipboard */}
            <button
              onClick={handleCopyToClipboard}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-orange-50 transition-colors text-right border-t border-orange-100"
            >
              {copied ? (
                <>
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-700">הועתק!</span>
                </>
              ) : (
                <>
                  <Copy className="h-5 w-5 text-orange-600" />
                  <span className="font-medium text-gray-700">העתק קישור</span>
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
